import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import DynamicSelect from "../../dynamic_select/DynamicSelect";
import { useFormContext } from "react-hook-form";
import { pricingSurfaceFor } from './pricingSurface';
import './billing.scss';

// An info icon beside a column heading, with the explanation on hover or keyboard focus.
//
// The box is position:FIXED and placed from the icon's own rect, which is the only thing that
// works here. The pricing table lives in a horizontally scrolling wrap, and a scroll container
// clips absolutely positioned descendants — the tooltip was being cut in half by it. Setting
// `overflow-y: visible` on the wrap does NOT help: CSS computes `visible` to `auto` when the
// other axis is auto or scroll, so the wrap kept clipping regardless. A fixed box is laid out
// against the viewport, so no ancestor's overflow can crop it. Nothing above it sets transform,
// filter or will-change, which would otherwise trap a fixed box in a new containing block.
//
// Exactly ONE tooltip mechanism, deliberately. There is no `title` attribute: a title renders
// the browser's own native tooltip, which showed alongside this box and gave two popups for one
// icon. Screen readers get the same text through aria-describedby, which announces without
// drawing anything, so the box stays in the DOM even while hidden.
//
// Tailwind's group-hover is not used either — this project is on Tailwind v1 with no config,
// where variants are opt-in per utility and only `opacity` ones were generated, so
// `group-hover:block` matched no rule at all.
const ColumnTooltip = ({ text, testId }) => {
    const describedBy = `${testId}-text`;
    const iconRef = useRef(null);
    const [position, setPosition] = useState(null);

    const show = () => {
        const rect = iconRef.current.getBoundingClientRect();
        // Clamped so an icon near either edge does not push the box off screen. Half the
        // box's max-width, which is what the -50% translate needs on each side.
        const halfBox = 130;
        const centre = rect.left + (rect.width / 2);

        setPosition({
            top: rect.bottom + 6,
            left: Math.min(Math.max(centre, halfBox + 8), window.innerWidth - halfBox - 8),
        });
    };

    const hide = () => setPosition(null);

    // A fixed box does not travel with the page, so it would detach from its icon the moment
    // anything scrolled. Hiding on scroll is simpler than tracking, and matches what a user
    // scrolling away expects anyway.
    useEffect(() => {
        if (!position) return undefined;

        window.addEventListener("scroll", hide, true);
        return () => window.removeEventListener("scroll", hide, true);
    }, [position]);

    return (
        <span className="billing-column-tooltip">
            <i
                ref={iconRef}
                className="fas fa-info-circle billing-column-tooltip-icon"
                data-testid={testId}
                tabIndex={0}
                role="button"
                aria-label="More information"
                aria-describedby={describedBy}
                onMouseEnter={show}
                onMouseLeave={hide}
                onFocus={show}
                onBlur={hide}
            />
            <span
                role="tooltip"
                id={describedBy}
                className={`billing-column-tooltip-box${position ? " is-visible" : ""}`}
                style={position ? { top: `${position.top}px`, left: `${position.left}px` } : undefined}
            >
                {text}
            </span>
        </span>
    );
};

// Matches the checkbox sizing the Bookings tab uses, so the settings tabs stay consistent.
const SCALED_CHECKBOX_STYLE = {
    transform: 'scale(3)',
    width: '15px',
    height: '15px',
    padding: '0',
    margin: '0',
    marginLeft: '15px',
    marginTop: '10px'
};

const Billing = ({
    selectedCompany,
    fetchCompany,
    currencyOptions,
    billingModelOptions,
}) =>{
    const {
        register,
        watch,
        setValue,
        reset,
        formState: { isDirty },
    } = useFormContext();

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        register('currency');
        register('billing_model');
        register('company_billings');
        register('demote_van_to_sedan');
        register('demote_van_to_sedan_max_passengers');
        register('demote_minibus_to_van');
    }, [register]);

    const [
        currency,
        billing_model,
        company_billings,
        demote_van_to_sedan,
        demote_van_to_sedan_max_passengers,
        demote_minibus_to_van
    ] = watch([
        'currency',
        'billing_model',
        'company_billings',
        'demote_van_to_sedan',
        'demote_van_to_sedan_max_passengers',
        'demote_minibus_to_van'
    ]);

    const companyBillings = company_billings ? JSON.parse(company_billings) : [];

    // Which table to draw, and what goes in it. All of that logic lives in
    // pricingSurface.js so it can be tested without rendering anything.
    const surface = pricingSurfaceFor(billing_model, companyBillings);

    // Save changes
    const updateCompany = async () => {
        setIsLoading(true);
        try {
            await axiosInstance(`/companies/${selectedCompany.id}.json`, {
                method: 'PUT',
                data: {
                    company: {
                        billing_model: billing_model,
                        currency: currency,
                        demote_van_to_sedan: demote_van_to_sedan,
                        // Blank means "no passenger limit", which the backend reads as
                        // "demote at any headcount" (FNB). It must go up as null, not "".
                        demote_van_to_sedan_max_passengers:
                            demote_van_to_sedan_max_passengers === '' || demote_van_to_sedan_max_passengers === undefined
                                ? null
                                : Number(demote_van_to_sedan_max_passengers),
                        demote_minibus_to_van: demote_minibus_to_van,
                    }
                }
            });

            await fetchCompany();

        } catch (error) {
            console.error("Company can't be updated.", error)
        } finally {
            setIsLoading(false);
        }
    };

    // Revert unsaved changes
    const handleRevertChanges = async () => {
        reset();
    };

    // Vehicle pricing changes
    const handleInputChange = (billingIndex, key, event) => {
        const updatedBillings = [...JSON.parse(company_billings)];
        updatedBillings[billingIndex] = {
            ...updatedBillings[billingIndex],
            [key]: event,
        };
        setValue('company_billings', JSON.stringify(updatedBillings), {
            shouldDirty: true
        });
    };

    // Save button in pricing table row
    const handleSubmit = async (billingIndex) => {
        // Get the billing values for specific car type
        const company_billing = JSON.parse(company_billings)[billingIndex] || {};

        // Save input value or set 0
        const updatedCompanyBilling = {
            cancelled: parseFloat(company_billing.cancelled) || 0,
            km0_km19: parseFloat(company_billing.km0_km19) || 0,
            km20_km29: parseFloat(company_billing.km20_km29) || 0,
            km30_km39: parseFloat(company_billing.km30_km39) || 0,
            km40_km49: parseFloat(company_billing.km40_km49) || 0,
            km50_km59: parseFloat(company_billing.km50_km59) || 0,
            km60_km69: parseFloat(company_billing.km60_km69) || 0,
            km70_km79: parseFloat(company_billing.km70_km79) || 0,
            km80_km89: parseFloat(company_billing.km80_km89) || 0,
            km90: parseFloat(company_billing.km90) || 0,
            // Fixed-only solo rates. Sent as 0 when blank, which pricing reads as "not set"
            // and falls back to the Any Distance rate — see PerSeatBilling::Pricing::Fixed.
            single_trip: parseFloat(company_billing.single_trip) || 0,
            adhoc_trip: parseFloat(company_billing.adhoc_trip) || 0,
            currency: currency || "za",
            app_fee: parseFloat(company_billing.app_fee) || 0,
            vat_percentage: parseFloat(company_billing.vat_percentage) || 0
        };

        setIsLoading(true);
        try {
            await axiosInstance({
                url: `/companies/${selectedCompany.id}/company_billings/${company_billing.id}.json`,
                method: 'PUT',
                data: {
                    company_billing: updatedCompanyBilling
                },
                headers: { Accept: 'application/json' }
            });

            await fetchCompany();
        } catch (error) {
            console.error("Failed to update company billing: ", error);
        } finally {
            setIsLoading(false);
        }
    };

    // Clear button in table row
    const handleClearChanges = () => {
        reset();
    };

    return (
        <div className="billing-tab w-full p-4 flex flex-col">

            {/* Billing Options section */}
            <div className="billing_options flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="billing-section-title">Billing Options</h3>
                        <p className="billing-section-subtitle">Details about billing</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col">
                    <div className="w-full flex flex-wrap">
                        <div className="w-full md:w-1/2 flex flex-col pr-1">
                            <label className="label font-medium text-gray-900">Billing Model</label>
                            <DynamicSelect
                                props={{
                                    onChange: (selected) => {
                                        setValue('billing_model', selected?.target.value?.value || '', {
                                            shouldDirty: true
                                        });
                                    },
                                    options: billingModelOptions,
                                    placeholder: "Select Billing Model",
                                    // No hidden input backs these (react-hook-form holds the
                                    // value), so the id has to be named explicitly or both
                                    // selects on this tab render as `undefined_select`.
                                    idPrefix: "billing_model",
                                    ariaLabel: "Billing Model",
                                    value: billing_model,
                                    closeMenuOnSelect: true
                                }}
                            />
                        </div>

                        <div className="w-full md:w-1/2 flex flex-col pl-1">
                            <label className="label font-medium text-gray-900">Company Currency</label>
                            <DynamicSelect
                                props={{
                                    onChange: (selected) => {
                                        setValue('currency', selected?.target.value?.value || '', {
                                            shouldDirty: true
                                        });
                                    },
                                    options: currencyOptions,
                                    placeholder: "Select Currency",
                                    idPrefix: "company_currency",
                                    ariaLabel: "Company Currency",
                                    value: currency,
                                    closeMenuOnSelect: true
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Billing Demotions section. Demotions bill one vehicle class at another
                class's rates, which only means something while pricing reads a vehicle
                rate — the Per Seat models never do, so the section is hidden rather than
                left on screen with no effect. */}
            {!surface.isPerSeat && (
                <>
                    <hr className="border border-gray-200 my-5" />

                    <div className="billing_demotions flex flex-wrap px-3 md:px-0" data-testid="billing-demotions-section">

                        <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                            <div className="w-full pr-5">
                                <h3 className="billing-section-title">Billing Demotions</h3>
                                <p className="billing-section-subtitle">
                                    Bill a vehicle at a lower class&apos;s rates. The trip still records the vehicle
                                    that actually drove it.
                                </p>
                            </div>
                        </div>

                        <div className="w-full md:w-3/4 flex flex-col">
                            <div className="w-full flex flex-col mb-4">
                                <label htmlFor="demote-van-to-sedan-checkbox" className="label font-medium text-gray-900">
                                    Bill Vans as Sedans
                                </label>
                                <input
                                    id="demote-van-to-sedan-checkbox"
                                    type="checkbox"
                                    data-testid="demote-van-to-sedan-checkbox"
                                    className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                    checked={demote_van_to_sedan ?? false}
                                    onChange={e => {
                                        setValue('demote_van_to_sedan', e.target.checked, { shouldDirty: true });
                                    }}
                                    style={SCALED_CHECKBOX_STYLE}
                                />
                            </div>

                            <div className="w-full md:w-1/2 flex flex-col mb-4">
                                <label
                                    htmlFor="demote-van-to-sedan-max-passengers-input"
                                    className="label font-medium text-gray-900"
                                >
                                    Only up to this many passengers
                                </label>
                                <input
                                    id="demote-van-to-sedan-max-passengers-input"
                                    type="number"
                                    min="1"
                                    step="1"
                                    data-testid="demote-van-to-sedan-max-passengers-input"
                                    className="input"
                                    placeholder="Leave blank to bill every van as a sedan"
                                    disabled={!demote_van_to_sedan}
                                    value={demote_van_to_sedan_max_passengers ?? ''}
                                    onChange={e => {
                                        setValue('demote_van_to_sedan_max_passengers', e.target.value, { shouldDirty: true });
                                    }}
                                />
                                <p className="billing-section-subtitle mt-1">
                                    Blank bills every van at sedan rates, however full it is. A number only bills
                                    the under-filled ones that way.
                                </p>
                            </div>

                            <div className="w-full flex flex-col">
                                <label htmlFor="demote-minibus-to-van-checkbox" className="label font-medium text-gray-900">
                                    Bill Minibuses as Vans
                                </label>
                                <input
                                    id="demote-minibus-to-van-checkbox"
                                    type="checkbox"
                                    data-testid="demote-minibus-to-van-checkbox"
                                    className="w-6 h-6 text-sm text-gray-800 scale-125 mr-4"
                                    checked={demote_minibus_to_van ?? false}
                                    onChange={e => {
                                        setValue('demote_minibus_to_van', e.target.checked, { shouldDirty: true });
                                    }}
                                    style={SCALED_CHECKBOX_STYLE}
                                />
                            </div>
                        </div>
                    </div>
                </>
            )}

            <hr className="border border-gray-200 my-5" />

            {/* Pricing section — the vehicle matrix, or a single per-seat row */}
            <div className="vehicle_pricing flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="billing-section-title" data-testid="billing-pricing-title">{surface.title}</h3>
                        <p className="billing-section-subtitle pb-3" data-testid="billing-pricing-subtitle">{surface.subtitle}</p>
                    </div>
                </div>

                <div className="vehicle_pricing overflow-visible rounded overflow-y-auto dark-gray-scrollbar w-full">
                    {/* The cell holds a per-passenger rate rather than a per-trip one, and
                        nothing else on the page says so. */}
                    {surface.note && (
                        <div className="billing-pricing-note mb-4" data-testid="billing-pricing-note">
                            <i className="fas fa-info-circle billing-pricing-note-icon" />
                            <div className="billing-pricing-note-text">{surface.note}</div>
                        </div>
                    )}

                    <div className="billing-pricing-table-wrap">
                        <table className="billing-pricing-table" data-testid="billing-pricing-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '150px' }}>{surface.leadColumnLabel}</th>
                                    {surface.columns.map((col) => (
                                        <th key={col.testKey || col.key}>
                                            <span className="billing-column-label">{col.label}</span>
                                            {col.tooltip && (
                                                <ColumnTooltip
                                                    text={col.tooltip}
                                                    testId={`billing-column-tooltip-${col.key}`}
                                                />
                                            )}
                                        </th>
                                    ))}
                                    <th style={{ width: '96px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {surface.rows.map((row) => {
                                    const billing = companyBillings[row.billingIndex] || {};
                                    return (
                                        <tr key={row.key}>
                                            <td className="billing-row-label">
                                                <div>{row.label}</div>
                                            </td>
                                            {surface.columns.map((col) => (
                                                <td key={col.testKey || col.key}>
                                                    <input
                                                        type="number"
                                                        data-testid={`billing-cell-${row.key}-${col.testKey || col.key}`}
                                                        value={billing[col.key] ?? ""}
                                                        className="billing-cell-input billing-input"
                                                        onChange={(e) => {
                                                            const value = e.target.value;
                                                            if (/^\d*\.?\d*$/.test(value)) {
                                                                handleInputChange(row.billingIndex, col.key, value);
                                                            }
                                                        }}
                                                    />
                                                </td>
                                            ))}

                                            <td>
                                                <div className="flex space-x-2 justify-end">
                                                    {/* Submit pricing values */}
                                                    <button
                                                        className="billing-row-action"
                                                        title="Save row"
                                                        data-testid={`billing-row-save-${row.key}`}
                                                        type="button"
                                                        onClick={()=> handleSubmit(row.billingIndex)}
                                                    >
                                                        <i className="fas fa-save" />
                                                    </button>

                                                    {/* Undo pricing changes */}
                                                    <button
                                                        className="billing-row-action billing-row-action--secondary"
                                                        title="Revert row"
                                                        type="button"
                                                        onClick={()=> handleClearChanges()}
                                                    >
                                                        <i className="fas fa-undo" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Save and revert buttons */}
            <div className="relative pt-20">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center relative cursor-pointer ${
                            isLoading ? "bg-gray-400" : "button"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        data-testid="billing-save-button"
                        onClick={()=> updateCompany()}
                        disabled={isLoading}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                        type="button"
                        className={`p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer bg-gray-500 hover:bg-gray-600`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={() => handleRevertChanges()}
                        disabled={isDirty == false}
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Billing;
